import { useEffect, useState } from 'react';

import { appParams } from '@playbooks/api';
import { Feedback } from '@playbooks/molecules/feedbacks';
import { SearchForm } from '@playbooks/molecules/forms';
import { Pagination } from '@playbooks/molecules/paginations';
import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Tabs, TabsList, TabsTrigger } from '@playbooks/ui/components/tabs';

import { useStore } from 'contexts';

const AgentSkillFlowSelect = ({
	selected,
	setSelected,
	onClose,
	onAdd,
	onEdit,
	onSave,
	libraryOnly = true,
	canManageResources = true,
}) => {
	const [skills, setSkills] = useState([]);
	const [loading, setLoading] = useState(false);
	const [params, setParams] = useState<any>({ ...appParams, view: 'public', pageSize: 10 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 10, totalRecords: 0 });
	const [error, setError] = useState('');
	const store = useStore();

	useEffect(() => {
		let active = true;
		setLoading(true);
		setError('');
		store
			.query({ url: libraryOnly ? '/workspace/skills' : '/agent-skills', params })
			.then(response => {
				if (!active) return;
				setSkills(response.data || []);
				setMeta(response.meta || { page: params.page, pageSize: params.pageSize, totalRecords: 0 });
			})
			.catch(nextError => active && setError(nextError?.message || 'Skills could not be loaded.'))
			.finally(() => active && setLoading(false));
		return () => {
			active = false;
		};
	}, [params, store]);

	const onSelect = skill => {
		const exists = selected.some(item => item.id === skill.id);
		setSelected(exists ? selected.filter(item => item.id !== skill.id) : [...selected, skill]);
	};
	const onView = view => setParams(current => ({ ...current, page: 0, view }));
	const emptyTitle = params.query
		? `No ${params.view} skills found`
		: params.view === 'private'
			? 'No private skills yet'
			: 'No public skills available';
	const onDelete = async skill => {
		if (!window.confirm(`Remove ${skill.name} from this workspace?`)) return;
		try {
			await store.deleteRecord({ url: `/agent-skills/${skill.uuid}` });
			setSkills(current => current.filter(item => item.id !== skill.id));
			setSelected(current => current.filter(item => item.id !== skill.id));
		} catch (nextError) {
			setError(nextError?.message || 'The skill could not be removed.');
		}
	};

	return (
		<>
			<DialogHeader>
				<DialogTitle>Choose skills</DialogTitle>
			</DialogHeader>
			<Tabs value={params.view} onValueChange={onView} className='gap-0 border-b px-4'>
				<TabsList>
					<TabsTrigger value='public'>Public</TabsTrigger>
					<TabsTrigger value='private'>Private</TabsTrigger>
				</TabsList>
			</Tabs>
			<div className='flex-between gap-3 border-b p-4'>
				<SearchForm
					id='agent_skill_flow_search'
					query={params.query || ''}
					setQuery={query => setParams({ ...params, page: 0, query })}
					placeholder='Search skills...'
					size='default'
					className='grow'
				/>
				<Button size='default' disabled={!canManageResources} onClick={onAdd}>
					Add New
				</Button>
			</div>
			<div className='min-h-96 py-2'>
				{loading ? (
					<Feedback
						layout='modal'
						icon='spinner'
						title='Loading skills'
						text={`Finding ${params.view} skills.`}
						className='min-h-80'
					/>
				) : error ? (
					<Feedback
						layout='modal'
						icon='triangle-exclamation'
						title='Skills unavailable'
						text={error}
						className='min-h-80'
					/>
				) : skills.length ? (
					<div className='grid gap-1'>
						{skills.map(skill => (
							<div key={skill.id} className='flex items-center gap-1'>
								<Button
									variant='ghost'
									data-active={selected.some(item => item.id === skill.id) || undefined}
									onClick={() => onSelect(skill)}
									className='h-auto min-h-12 min-w-0 grow justify-start px-3 py-2 text-left'>
									<span className='min-w-0 grow'>
										<span className='block truncate text-sm font-medium'>{skill.name}</span>
										<span className='block truncate text-xs font-normal text-foreground-body'>{skill.description}</span>
										<span className='block truncate text-xs font-normal text-foreground-subtle'>
											{skill.owner?.name || 'Playbooks'} · {skill.sourceType}
										</span>
									</span>
								</Button>
								{skill.isOwner && canManageResources ? (
									<Button variant='ghost' size='sm' onClick={() => onEdit(skill)}>
										Edit
									</Button>
								) : null}
								{skill.isOwner && canManageResources ? (
									<Button variant='ghost' size='sm' onClick={() => onDelete(skill)}>
										Remove
									</Button>
								) : null}
							</div>
						))}
					</div>
				) : (
					<Feedback
						layout='modal'
						icon='sparkles'
						title={emptyTitle}
						text={
							params.query
								? 'Try a different search.'
								: params.view === 'private'
									? 'Create or import a private skill for this workspace.'
									: 'Public agent skills will appear here.'
						}
						className='min-h-80'
					/>
				)}
			</div>
			<div className='border-t p-3'>
				<Pagination
					layout='table'
					meta={meta}
					params={params}
					setParams={setParams}
					taskRunning={loading}
					totalLabel='skills'
				/>
			</div>
			<DialogFooter>
				<Button variant='secondary' onClick={onClose}>
					Close
				</Button>
				<Button onClick={onSave}>Save</Button>
			</DialogFooter>
		</>
	);
};

export { AgentSkillFlowSelect };
